import { useEffect, useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import { useIsVisible } from '@/hooks/useIsVisible';
import { useLimuloPolling, useLimuloPollingGuida } from '@/hooks/useLimuloPolling';
import { AVVISO_MS, useEsitiSfide, useRispostaSfida, type AnnotaSfida, type Nota } from '@/hooks/useRispostaSfida';
import type { LimuloAzionePendente } from '@/lib/limulo/azionePersistente';
import { CHIAVE_COLLEGANDO_PROFILO, SFIDE_IN_ARRIVO, nomeDiChiSfida, sfidaDaRispondere } from '@/lib/limulo/challenge';
import { LIMULO_POLL_SFIDE_MS } from '@/lib/limulo/polling';
import { gameByCode, labelOf } from '@/lib/limulo/profiles';
import { rigaSfidaPiattaforma } from '@/lib/limulo/sfideBarra';
import { useSfideGestite } from '@/lib/limulo/sfideGestite';
import { useGetLimuloChallengesQuery, useGetLimuloGamesQuery } from '@/services/limuloApi';
import { useAppSelector } from '@/store/hooks';
import type { LimuloChallengeWithIdentities } from '@/types/limulo';

/**
 * **L'UNICA fonte della barra: le sfide di PIATTAFORMA in arrivo** (T2-5, lotto B1; dal lotto C2 non ce
 * n'e' un'altra). Chi riceve una sfida creata con `POST /v1/challenges` la vede nella barra delle azioni
 * con Accetta e Rifiuta. Il provider (`SfideBarraContext.tsx`) la monta con la serratura di piattaforma
 * (Limulo + ruolo beta) e raccoglie le righe con `riporta(chiave, riga)`.
 *
 * 🔑 **Il costo, MISURATO in `tests/sfide-barra-piattaforma-costo.test.tsx`** (non solo dichiarato):
 *  · UNA `GET /v1/challenges?direction=in&state=pending&limit=20` ogni 60 s (`LIMULO_POLL_SFIDE_MS`),
 *    su OGNI schermata, perche' il provider sta in `AppShell`;
 *  · zero fuori serratura (il componente non si monta), zero senza sessione (`skip`), zero a pagina
 *    nascosta (`useIsVisible` toglie il timer: `skipPollingIfUnfocused` da solo ne lascia partire UNO)
 *    e UNA rilettura al ritorno (`refetchOnFocus`, acceso su tutto `limuloApi`);
 *  · un fallimento rallenta (60 s per i primi quattro giri, poi 120 s: `lib/pollingBackoff.ts`, misurato) e ritenta al piu' UNA volta
 *    dentro la stessa lettura, solo su rete/5xx (`limuloBaseQuery`); un 404/424 non si ritenta;
 *  · SOLO se c'e' almeno una sfida da rispondere: una `GET /v1/games` e una `GET /v1/games/profiles`
 *    (una volta, cache condivisa con le altre schermate), per il nome del gioco e per il profilo con
 *    cui accettare. Nessuna sfida ⇒ nessuna delle due.
 * ⛔ Indipendente da tornei e coda: e' una query a se', e se fallisce (404 dove la rotta non e'
 * servita) le altre righe della barra non se ne accorgono.
 */
type Riporta = (chiave: string, riga: LimuloAzionePendente | null) => void;

export function SorgenteSfidePiattaforma({ riporta }: { riporta: Riporta }) {
  const authed = useAppSelector((s) => s.auth.bridgeAuthenticated);
  const polling = useLimuloPolling();
  // 🔴 Nascosta = ZERO, non «al piu' uno»: `skipPollingIfUnfocused` di RTK salta il giro, ma legge lo
  // stato di quando il timer e' stato ARMATO (`startNextPoll`, `const state = getState()` fuori dal
  // `setTimeout`): il giro gia' in attesa parte una volta dopo che la pagina e' andata in secondo
  // piano — misurato in `tests/sfide-barra-piattaforma-costo.test.tsx` (una GET a +60 s da nascosta).
  // Con `pollingInterval: 0` il timer si toglie subito; al ritorno RTK rilegge UNA volta
  // (`refetchOnFocus`) e il ritmo riparte.
  const visibile = useIsVisible();
  const lista = useGetLimuloChallengesQuery(
    SFIDE_IN_ARRIVO,
    { skip: !authed, ...polling.ritmo, pollingInterval: visibile ? polling.ritmo.pollingInterval : 0 },
  );
  useLimuloPollingGuida(polling, lista, LIMULO_POLL_SFIDE_MS);
  const items = useMemo(() => (authed ? (lista.data?.items ?? []) : []), [authed, lista.data]);

  // ⛔ Nessun orologio: la riga c'e' finche' il server dice `pending` (`sfidaDaRispondere`). Se e' scaduta
  // fra due giri, il rifiuto `sfida.scaduta` lo dice a schermo e la riga se ne va.
  //
  // Cio' che il giocatore HA GIA' FATTO sta QUI e non nella sonda: la sonda sparisce quando la sfida esce
  // dalla lista (l'Accetta invalida la lista, che rilegge subito; un 503 puo' nascondere un'accettazione
  // riuscita), e con lei sparirebbe l'unico segnale che il tocco e' andato — «chi vede non la legge»
  // (bloccante 3 di #489).
  //  · `gestite` (store di MODULO, `lib/limulo/sfideGestite`): le sfide su cui il giocatore ha agito, per
  //    tutta la pagina — sopravvive a un rimontaggio del provider. Una lettura ancora vecchia che le dice
  //    `pending` non le rimette in offerta.
  //  · `note`: l'esito, VISIBILE nella riga. `ok`/`chiusa` restano `AVVISO_MS` e chiudono la sfida;
  //    `errore` (un guasto: la sfida puo' essere ancora li') resta finche' la riga e' viva e, se la sfida
  //    esce dalla lista, `AVVISO_MS` da quel momento.
  const gestite = useSfideGestite();
  const vive = useMemo(
    () => items.filter((c) => sfidaDaRispondere(c) && !gestite.has(c.challengeId)),
    [items, gestite],
  );
  const viveIds = useMemo(() => new Set(vive.map((c) => c.challengeId)), [vive]);
  // Gli esiti stanno QUI, nel padre, e non nella sonda (v. `useEsitiSfide`).
  const { note, annota, togli } = useEsitiSfide(viveIds);

  return (
    <>
      {vive.map((c) => {
        const n = note[c.challengeId];
        return (
          <SondaSfidaPiattaforma
            key={c.challengeId}
            sfida={c}
            errore={n?.tipo === 'errore' ? n.testo : null}
            avviso={null}
            annota={annota}
            azzera={togli}
            riporta={riporta}
          />
        );
      })}
      {Object.values(note)
        .filter((n) => n.tipo !== 'errore' || !viveIds.has(n.sfida.challengeId))
        .map((n) => (
          <SondaSfidaPiattaforma
            key={n.sfida.challengeId}
            sfida={n.sfida}
            errore={null}
            avviso={n}
            annota={annota}
            azzera={togli}
            riporta={riporta}
          />
        ))}
    </>
  );
}

/** Ri-esportata: i banchi di B1 la importano da qui, e vive in `hooks/useRispostaSfida` (la usa anche il tab). */
export { AVVISO_MS };

/**
 * Una sfida in arrivo → una riga della barra. I tocchi (Accetta, Rifiuta, la scelta del profilo, gli esiti)
 * sono di `useRispostaSfida`, gli stessi del tab «Sfide».
 */
function SondaSfidaPiattaforma({
  sfida,
  errore,
  avviso,
  annota,
  azzera,
  riporta,
}: {
  sfida: LimuloChallengeWithIdentities;
  /** L'errore dell'ultimo tocco (tenuto dal padre: sopravvive alla sonda), a riga ancora offerta. */
  errore: string | null;
  /** Non `null` = la riga non e' piu' offerta: mostra l'esito coi pulsanti spenti. */
  avviso: Nota | null;
  annota: AnnotaSfida;
  azzera: (challengeId: string) => void;
  riporta: Riporta;
}) {
  const { t } = useTranslation('general');
  const giochi = useGetLimuloGamesQuery(undefined);
  const { onAccetta, onRifiuta, accettando, rifiutando, inLettura, collegando } = useRispostaSfida(sfida, annota, azzera);

  const { challengeId } = sfida;
  const nome = nomeDiChiSfida(sfida);
  const nomeGioco = labelOf(gameByCode(sfida.gameId, giochi.data ?? []), sfida.gameId);
  const frase = t('social.sfida_ricevuta_testo', {
    name: nome ?? t('social.sfida_ricevuta_senza_nome', { defaultValue: 'A player' }),
    game: nomeGioco,
  });
  const chiave = `sfida-piattaforma:${challengeId}`;
  // Con un avviso il giocatore ha GIA' agito (o la sfida e' sparita dalla lista): pulsanti spenti, e l'esito si
  // LEGGE sotto la riga, in ogni modo — un successo come `role="status"`, un «non c'e' piu'» o un guasto come
  // `role="alert"`. Il titolo resta la frase: in modo `riga` il titolo non si vede, e non puo' portare l'esito.
  const esitoVisibile = avviso?.tipo === 'ok' ? avviso.testo : null;
  // kb#388: mentre il tocco aspetta il sync del profilo la riga lo dice con la frase NEUTRA (`collegando`, non l'`esito` verde), e mai
  // sopra un esito o un errore.
  const testoCollegando = collegando && errore === null && avviso === null ? t(CHIAVE_COLLEGANDO_PROFILO) : null;
  const testoErrore = avviso !== null && avviso.tipo !== 'ok' ? avviso.testo : errore;
  const riga = useMemo(
    () =>
      rigaSfidaPiattaforma({
        challengeId,
        testo: frase,
        nomeSfidante: nome,
        scadenza: sfida.expiresAt,
        onAccetta,
        onRifiuta,
        // La lista dei profili in lettura tiene spento Accetta come un'accettazione in corso.
        accettando: accettando || inLettura || avviso !== null,
        rifiutando,
        errore: testoErrore,
        esito: esitoVisibile,
        collegando: testoCollegando,
      }),
    [challengeId, frase, nome, sfida.expiresAt, onAccetta, onRifiuta, accettando, inLettura, avviso, rifiutando, testoErrore, esitoVisibile, testoCollegando],
  );
  useEffect(() => {
    riporta(chiave, riga);
  }, [chiave, riga, riporta]);
  // Una sfida che esce dalla lista porta via la sua riga (come `SondaTorneo`).
  useEffect(() => () => riporta(chiave, null), [chiave, riporta]);
  return null;
}
